/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { sendDataLayer, track } from "jiffy-analytics";
import { notifyError } from "common/utils/app-monitoring/notify";
import { generateRandomString } from "common/utils/helpers/random";
import productPage from "../product-details-quantity-form/product-page";

export const PDP_PAGE_VISIT = "PDPPageVisit";
export const ADD_TO_CART = "AddToCart";

type DtfProduct = {
  brand: string;
  catalog_number: string;
  adwords_category: string;
  retail_price: string | number;
  business_line?: string | null;
  main_product_category?: string | null;
};

type DtfVariant = {
  sku: string;
  currency: string;
  composition_id?: number | null;
};

type FbData = {
  external_id?: string[];
  [key: string]: unknown;
};

type AppConfig = {
  product: DtfProduct;
  variant: DtfVariant;
  fbData: FbData;
};

const SELECTORS = {
  DTF_CONTAINER: "#vue-product-details-dtf",
  PRODUCT_CONTAINER: "#product-page-container"
};

const EVENT_ID_PREFIX = {
  VIEW_CONTENT: "VC",
  PAGE_VIEW: "PV"
};

const getElements = {
  get $dtfContainer() {
    return document.querySelector(SELECTORS.DTF_CONTAINER) as HTMLElement | null;
  },

  get $productContainer() {
    return document.querySelector(SELECTORS.PRODUCT_CONTAINER) as HTMLElement | null;
  },

  get $formEventId() {
    return document.querySelector(productPage.selectors.formEventId) as HTMLInputElement | null;
  }
};

/**
 * Get app configuration from DTF container dataset
 * @returns {Object} Configuration object with product, variant, and FB data
 */
const getAppConfig = (): AppConfig | undefined => {
  const $container = getElements.$dtfContainer;

  if (!$container?.dataset?.["appConfig"]) {
    console.error("DTF container or app config not found");
    return undefined;
  }

  try {
    const config = JSON.parse($container.dataset["appConfig"]);
    return {
      product: config.product,
      variant: config.variant,
      fbData: JSON.parse(config.fb_data || "{}")
    };
  } catch (error) {
    console.error("Failed to parse app config:", error);
    return undefined;
  }
};

/**
 * Generate product display name
 */
const getProductName = (product: DtfProduct) => `${product.brand} ${product.catalog_number}`;

/**
 * Build Facebook content object
 */
const buildFbContent = (product: DtfProduct, fbData: FbData, contentId: string) => ({
  content_ids: [contentId],
  content_name: getProductName(product),
  content_category: product.adwords_category,
  content_type: "product",
  user_data: fbData
});

/**
 * Safely parse price value
 * @param {*} price - Price value to parse
 * @returns {number} Parsed price or 0 if invalid
 */
const parsePrice = (price: string | number) => Number(price) || 0;

/**
 * Build item viewed object for analytics
 */
const buildItemsViewed = (product: DtfProduct, variant: DtfVariant) => [
  {
    name: getProductName(product),
    id: variant.sku,
    brand: product.brand,
    price: product.retail_price,
    google_business_vertical: "retail"
  }
];

/**
 * Build ecommerce data object
 */
export const buildEcommerceData = (product: DtfProduct, variant: DtfVariant) => ({
  currency: variant.currency,
  value: parsePrice(product.retail_price),

  items: [
    {
      item_id: variant.sku,
      item_name: getProductName(product),
      item_brand: product.brand,
      item_category: product.adwords_category,
      price: product.retail_price
    }
  ]
});

/**
 * Generate event ID with prefix and timestamp
 */
const generateEventId = (prefix: string, userId: string) => `${prefix}-${userId}-${Date.now()}`;

/**
 * Build Mixpanel product_viewed payload. Mirrors the shape of Blanks PDPs
 * (see frontend/components/jiffy/product-details-delivery-date-simple/track.js)
 * for the subset of fields DTF can populate from appConfig alone.
 */
const buildProductViewedPayload = (product: DtfProduct, variant: DtfVariant) => ({
  brand: product.brand,
  product: `${product.brand} ${product.catalog_number}`,
  product_category: product.adwords_category,
  main_product_category: product.main_product_category,
  business_line: product.business_line,
  spree_composition_id: variant.composition_id
});

/**
 * Track product detail page visit
 */
export const trackPDPPageVisit = () => {
  const config = getAppConfig();
  if (!config) return;

  const { product, variant, fbData } = config;

  try {
    track("product_viewed", buildProductViewedPayload(product, variant));
  } catch (error) {
    notifyError(error instanceof Error ? error : new Error(String(error)), {
      context: {
        source: "dtf-ga-tracker.trackPDPPageVisit",
        event: "product_viewed",
        catalog_number: product.catalog_number,
        business_line: product.business_line
      }
    });
  }

  const userIdentifier = fbData.external_id?.[0] || generateRandomString();

  const dataLayerPayload = {
    event: PDP_PAGE_VISIT,
    items: [{ product_id: variant.sku }],
    msAdProductIds: [variant.sku],
    viewItemValue: product.retail_price,
    pageViewEventId: generateEventId(EVENT_ID_PREFIX.PAGE_VIEW, userIdentifier),
    viewContentEventId: generateEventId(EVENT_ID_PREFIX.VIEW_CONTENT, userIdentifier),
    fbContent: buildFbContent(product, fbData, variant.sku),
    itemsViewed: buildItemsViewed(product, variant),
    ecommerce: buildEcommerceData(product, variant)
  };

  sendDataLayer(dataLayerPayload);
};

/**
 * Track add to cart event
 * @param {number} quantity - Quantity of items added
 * @param {number} price - Total price of items
 */
export const trackAddToCart = (quantity: number, price: number | string) => {
  const config = getAppConfig();
  if (!config) return;

  const { product, variant, fbData } = config;

  const addToCartEventId = getElements.$formEventId?.value;
  const email = getElements.$productContainer?.dataset?.["orderEmail"];

  const itemsAdded = {
    id: variant.sku,
    name: getProductName(product),
    brand: product.brand,
    price: product.retail_price,
    currency: variant.currency,
    sku: variant.sku,
    quantity
  };

  const dataLayerPayload = {
    event: ADD_TO_CART,
    itemsAdded,
    email,
    addToCartEventId,
    addToCartValue: price,
    fbContent: buildFbContent(product, fbData, variant.sku),
    ecommerce: buildEcommerceData(product, variant)
  };

  sendDataLayer(dataLayerPayload);
};
